import { useState } from "react";
import { DateField } from "@/components/common/masked-form-field";
import { FormField } from "@/components/common/form-field";
import type { PortalCollectionApi } from "@/features/portal/api";
import {
  CollectionCard,
  type Column,
  type PortalRecord,
  usePortalCollection,
} from "../collection-card";
import { PortalCrudDialog } from "../crud-dialog";
import { CheckboxInput, MoneyInput, SelectInput } from "../fields";
import {
  type FieldSpec,
  type FormValues,
  initialFromRecord,
  payloadFromValues,
  schemaFor,
} from "./spec";

/** Render one spec field as its matching Formik-bound input. */
function SpecField({ field, editing }: { field: FieldSpec; editing: boolean }) {
  switch (field.kind) {
    case "money":
      return <MoneyInput name={field.name} label={field.label} />;
    case "select":
      return <SelectInput name={field.name} label={field.label} options={field.options ?? []} />;
    case "checkbox":
      return <CheckboxInput name={field.name} label={field.label} />;
    case "date":
      return <DateField name={field.name} label={field.label} />;
    case "number":
      return <FormField name={field.name} label={field.label} inputMode="numeric" />;
    case "secret":
      return (
        <FormField
          name={field.name}
          label={field.label}
          placeholder={editing ? "Leave blank to keep" : ""}
        />
      );
    default:
      return <FormField name={field.name} label={field.label} />;
  }
}

function SpecDialog({
  record,
  open,
  onOpenChange,
  onSaved,
  api,
  noun,
  fields,
}: {
  record: PortalRecord | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
  api: PortalCollectionApi;
  noun: string;
  fields: FieldSpec[];
}) {
  const editing = !!record;

  async function onSave(values: FormValues) {
    const payload = payloadFromValues(values, fields);
    if (editing && record) await api.update(record.id, payload);
    else await api.create(payload);
  }

  return (
    <PortalCrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={editing}
      noun={noun}
      className="sm:max-w-2xl"
      initialValues={initialFromRecord(record, fields)}
      validationSchema={schemaFor(fields)}
      onSave={onSave}
      onSaved={onSaved}
    >
      {fields.map((f) => (
        <SpecField key={f.name} field={f} editing={editing} />
      ))}
    </PortalCrudDialog>
  );
}

/**
 * A spec-driven CRUD collection card for the limited-field 433-B business
 * surfaces. Give it a `PortalCollectionApi`, list `columns`, and a `fields`
 * spec — it renders the list plus a matching add/edit dialog, handling the
 * cents/date/secret conversions in one place (see `./spec`).
 */
export function BusinessCollection({
  api,
  title,
  addLabel,
  noun,
  columns,
  fields,
  query,
  description,
}: {
  api: PortalCollectionApi;
  title: string;
  addLabel: string;
  noun: string;
  columns: Column<PortalRecord>[];
  fields: FieldSpec[];
  query?: string;
  description?: string;
}) {
  const { rows, loading, reload } = usePortalCollection(
    api as PortalCollectionApi<PortalRecord>,
    query,
  );
  const [dialog, setDialog] = useState<{ open: boolean; record: PortalRecord | null }>({
    open: false,
    record: null,
  });

  return (
    <>
      <CollectionCard
        title={title}
        description={description}
        addLabel={addLabel}
        columns={columns}
        rows={rows}
        loading={loading}
        onAdd={() => setDialog({ open: true, record: null })}
        onEdit={(record) => setDialog({ open: true, record })}
        onDelete={(row) => api.remove(row.id)}
        onDeleted={reload}
      />
      <SpecDialog
        record={dialog.record}
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        onSaved={reload}
        api={api}
        noun={noun}
        fields={fields}
      />
    </>
  );
}
